"use client";

import React, { useState } from "react";
import { Hammer, ScanEye, Cpu, WandSparkles, Rocket, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Link } from "@/components/common/Link";
import { motion } from "framer-motion";
interface WorkflowStage {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
}
const stages: WorkflowStage[] = [{
  id: "insight",
  title: "EINBLICK",
  description: "Wir analysieren Deine Geschäfts-ziele und Zielgruppen, um daraus einen Bauplan für Deine Medien-inhalte zu entwickeln.",
  icon: ScanEye
}, {
  id: "hammering",
  title: "SCHMIEDEN",
  description: "Das initiale Konzept wird in Form gegossen und wir schmieden einen ersten Rohentwurf Deiner Bilder, Videos, Tutorials und Guides.",
  icon: Hammer
}, {
  id: "refining",
  title: "VERFEINERN",
  description: "Unsere KI veredelt iterativ das Rohmaterial - für mehr Klarheit, konsistentes Branding und eine stärkere visuelle Wirkung.",
  icon: Cpu
}, {
  id: "polish",
  title: "POLIEREN",
  description: "Wir geben den Inhalten den finalen Glanz - damit alles Deinen hohen Erwartungen entspricht.",
  icon: WandSparkles
}, {
  id: "delivery",
  title: "LIEFERUNG",
  description: "Du erhältst Deine fertigen Medien - bereit, Deine Zielgruppe zu begeis-tern und messbares Wachstum zu erzielen.",
  icon: Rocket
}];
const ParticleBackground = () => {
  const particles = Array.from({
    length: 50
  }, (_, i) => ({
    id: i,
    delay: Math.random() * 5,
    duration: 10 + Math.random() * 10,
    x: Math.random() * 100,
    char: ['0', '1', '<', '>', '/', '{', '}', ';', 'AI', '[]'][Math.floor(Math.random() * 10)]
  }));
  return <div className="absolute inset-0 z-0 opacity-30 pointer-events-none overflow-hidden">
      {particles.map(particle => <motion.div key={particle.id} className="absolute text-blue-400 font-mono text-xs" initial={{
      x: `${particle.x}%`,
      y: '110%',
      opacity: 0.3
    }} animate={{
      y: '-10%',
      opacity: [0.3, 0.6, 0.3]
    }} transition={{
      duration: particle.duration,
      repeat: Infinity,
      delay: particle.delay,
      ease: "linear"
    }}>
          {particle.char}
        </motion.div>)}
    </div>;
};
const TimelineNode = ({
  stage,
  index,
  total
}: {
  stage: WorkflowStage;
  index: number;
  total: number;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const isLast = index === total - 1;
  return <div className="relative flex flex-col items-center group z-10" data-index={index} onMouseEnter={() => setIsOpen(true)} onMouseLeave={() => setIsOpen(false)}>
      {/* Node with Popover */}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" className="relative w-auto h-auto p-0 rounded-full hover:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0" aria-label={`View details for ${stage.title}`}>
            {/* Icon Circle */}
            <div className={`
              w-16 h-16 md:w-20 md:h-20 rounded-full 
              flex items-center justify-center
              bg-gradient-to-br from-slate-700 to-slate-900
              border-2 border-slate-500
              shadow-[0_0_15px_rgba(100,150,255,0.3)]
              transition-all duration-300 ease-out
              group-hover:scale-110 group-hover:border-primary group-hover:shadow-[0_0_25px_var(--color-primary)]
              z-20
            `}>
              {/* Icon */}
              <stage.icon className="w-8 h-8 md:w-10 md:h-10 text-slate-300 group-hover:text-white transition-colors duration-300" strokeWidth={1.5} />
            </div>
            
            {/* Connecting Line (Mobile Only) */}
           {/* Connecting Line (Mobile Only) – deaktiviert */}
{/* {!isLast && <div className="absolute top-full left-1/2 -translate-x-1/2 w-1 h-16 bg-gradient-to-b from-secondary to-primary md:hidden -z-10" />} */}
          </Button>
        </PopoverTrigger>
        
        <PopoverContent side="top" className="w-64 border-primary/30 bg-card/95 backdrop-blur-md text-card-foreground shadow-xl">
          <div className="space-y-2">
            <h4 className="font-bold text-lg leading-none text-primary flex items-center gap-2">
              {stage.title}
            </h4>
            <p className="text-sm text-muted-foreground">
              {stage.description}
            </p>
          </div>
        </PopoverContent>
      </Popover>

      {/* Stage Label */}
      <div className="mt-4 text-center">
        <h3 className="text-sm md:text-base font-bold tracking-wider uppercase text-slate-300 group-hover:text-primary transition-colors">
          {stage.title}
        </h3>
        <span className="text-xs text-slate-500 font-mono">Step 0{index + 1}</span>
      </div>
    </div>;
};
export default function TheForgeWorkflow() {
  return <section id="the-forge-workflow" className="dark bg-background text-foreground relative py-24 overflow-hidden min-h-[800px] flex flex-col justify-center">
      {/* Particle Background */}
      <ParticleBackground />

      <div className="container relative z-10 mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/10 border border-secondary/20 text-secondary text-xs font-mono mb-4">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-secondary opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-secondary"></span>
            </span>
            KI-UNTERSTÜTZE PORDUKTION
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white via-slate-200 to-slate-400">Der Schmiede-Prozess</h2>
          
          <p className="text-lg text-muted-foreground">Vom ersten Funken bis zur polierten Auslieferung - Unser KI-gestützter Workflow verwandelt Konzepte schnell und präzise in professionelle Medieninhalte zu geringen Kosten.</p>
        </div>

        {/* Workflow Timeline */}
        <div className="relative max-w-6xl mx-auto">
          
          {/* Horizontal Progress Bar (Desktop) */}
          <div className="hidden md:block absolute top-10 left-0 w-full h-1 bg-slate-800 rounded-full overflow-hidden">
            <div className="w-full h-full bg-gradient-to-r from-secondary via-primary to-secondary opacity-50 blur-[2px]" />
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-secondary via-primary to-secondary animate-pulse" />
          </div>

          {/* Timeline Nodes */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-12 md:gap-4">
            {stages.map((stage, i) => <TimelineNode key={stage.id} stage={stage} index={i} total={stages.length} />)}
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-24 text-center">
          <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-[0_0_20px_rgba(100,150,255,0.4)] hover:shadow-[0_0_30px_rgba(100,150,255,0.6)] transition-all duration-300" asChild>
            <Link to="/ContactPage">Starte Dein Projekt<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-chevron-right ml-2 h-4 w-4" aria-hidden="true" data-ast-id="f:/src/pages/Home/sections/TheForgeWorkflow.tsx|sig:61fh1g" data-ast-start="7459" data-ast-end="7500"><path d="m9 18 6-6-6-6"></path></svg></Link>
          </Button>
          <p className="mt-4 text-sm text-muted-foreground">Bist Du bereit Deine Content-Produktion zu steigern und Geld zu sparen?</p>
        </div>
      </div>
    </section>;
}